/**
 * Apache route whitelist for the React app.
 *
 * react-snap writes build/<route>/index.html for every path in PRERENDER_ROUTES
 * (package.json reactSnap.include must match). The homepage is the exception:
 * it overwrites build/index.html, and the empty Create React App shell is kept
 * as build/200.html.
 *
 * public/.htaccess (copied into build/) and the repo root .htaccess do not
 * name each prerendered path. They serve <route>/index.html when that file
 * exists. A new prerendered page is therefore served on the next build once
 * it is listed here and in reactSnap.include.
 *
 * SPA_SHELL_ROUTES have no snapshot. Apache rewrites only /ai-city to
 * 200.html, and that shell is noindex. Unknown /services, /capabilities, and
 * /service URLs are not rewritten. They use ErrorDocument 404 /404.html.
 *
 * When you add a page:
 * 1. Add the <Route> in src/App.tsx.
 * 2. Add the path to PRERENDER_ROUTES or SPA_SHELL_ROUTES below.
 * 3. If the HTML should contain the rendered page, add the same path to
 *    package.json reactSnap.include.
 * npm run build runs scripts/check-known-routes.js and exits non-zero if
 * App.tsx, this list, and reactSnap.include disagree.
 *
 * sitemap.xml is built from INDEXABLE_ROUTES. Each of those paths needs a
 * lastmod in bti-admin-panel/config/sitemap-static.json. The same check
 * fails when that file drifts. Do not put /sitemap, /ai-city, or /404 in it.
 *
 * Leave /api, /admin, /super-bti-login, /blog, /bti-admin-panel, and /storage
 * out of these lists. The htaccess files forward the first four to Laravel
 * and do not map the admin panel or storage onto the React app.
 */

export const SITE_ORIGIN = 'https://browntech.co';

export const PRERENDER_ROUTES = [
  '/',
  '/about',
  '/contact',
  '/portfolio',
  '/blogs',
  '/faq',
  '/hiring',
  '/products',
  '/services/staff-augmentation',
  '/services/product-studio',
  '/services/project-sprints',
  '/services/it-outsourcing',
  '/services/custom-software-development',
  '/services/gohighlevel-expert',
  '/capabilities/ai',
  '/capabilities/ai-sales',
  '/capabilities/mobile-development',
  '/capabilities/web-development',
  '/capabilities/ui-ux',
  '/capabilities/cloud-solution',
  '/sitemap',
  '/404',
] as const;

/** Routes whose snapshot should be a public 200. /404 is only the error document. */
export const PUBLIC_PRERENDER_ROUTES = PRERENDER_ROUTES.filter((route) => route !== '/404');

/** /ai-city only. It is noindex. Do not add indexable pages here. */
export const SPA_SHELL_ROUTES = ['/ai-city'] as const;

/**
 * Left out of sitemap.xml.
 * /sitemap is an indexable HTML directory with its own canonical. It is omitted
 * from the XML sitemap so the file does not list the directory page.
 * /ai-city is the noindex demo shell. /404 is the error document.
 */
export const SITEMAP_EXCLUDED_ROUTES = ['/sitemap', '/ai-city', '/404'] as const;

/** Prerendered pages search engines should crawl. No noindex and no 404. */
export const INDEXABLE_ROUTES = PRERENDER_ROUTES.filter(
  (route) => !(SITEMAP_EXCLUDED_ROUTES as readonly string[]).includes(route)
);

export function normalizePath(pathname: string): string {
  if (!pathname) return '/';
  const clean = pathname.split('?')[0].split('#')[0];
  if (clean.length > 1 && clean.endsWith('/')) return clean.slice(0, -1);
  return clean || '/';
}

/** Absolute, no trailing slash. The homepage is https://browntech.co */
export function toAbsoluteCanonical(pathname: string): string {
  const path = normalizePath(pathname);
  if (path === '/') return SITE_ORIGIN;
  return `${SITE_ORIGIN}${path.startsWith('/') ? path : `/${path}`}`;
}

export function isKnownSpaPath(pathname: string): boolean {
  const path = normalizePath(pathname);
  if ((PUBLIC_PRERENDER_ROUTES as readonly string[]).includes(path)) return true;
  if ((SPA_SHELL_ROUTES as readonly string[]).includes(path)) return true;
  return false;
}
